// src/domains/core/dashboard/hooks/useRoadmapTopTasks.ts

import type {
  Roadmap_Item,
  Roadmap_Item_Score,
  Roadmap_Item_Priority,
} from "@/domains/admin/roadmap/types";

import { useMemo } from "react";

import { useRoadmapItems } from "@/domains/admin/roadmap/hooks/useRoadmapItems";

/**
 * Roadmap Task with calculated score
 */
export interface RoadmapTaskWithScore {
  rank: number;
  score: number;
  task: Roadmap_Item;
}

/**
 * Score value mapping
 *
 * @param score - Priority or score value to map
 *
 * @returns {number} Numeric score value (0-3)
 *
 */
const getScoreValue = (score?: "high" | "low" | "medium"): number => {
  switch (score) {
    case "high": {
      return 3;
    }
    case "medium": {
      return 2;
    }
    case "low": {
      return 1;
    }
    default: {
      return 0;
    }
  }
};

/**
 * Calculate combined score for a roadmap task
 * Score = Impact + Priority - Effort
 * Higher score = higher priority
 *
 * @param task - Roadmap task item
 *
 * @returns {number} Calculated score value
 *
 */
const calculateTaskScore = (task: Roadmap_Item): number => {
  const impactScore: Roadmap_Item_Score | undefined = task.impact_score;
  const priorityScore: Roadmap_Item_Priority | undefined = task.priority;
  const effortEstimate: Roadmap_Item_Score | undefined = task.effort_estimate;

  const impact = getScoreValue(
    impactScore === "high" || impactScore === "medium" || impactScore === "low"
      ? impactScore
      : undefined,
  );
  const priority = getScoreValue(
    priorityScore === "high" || priorityScore === "medium" || priorityScore === "low"
      ? priorityScore
      : undefined,
  );
  const effort = getScoreValue(
    effortEstimate === "high" || effortEstimate === "medium" || effortEstimate === "low"
      ? effortEstimate
      : undefined,
  );

  // Score = Impact + Priority - Effort
  // Note: For effort, we invert the logic (high effort = lower score)
  // So: high effort = 1, medium = 2, low = 3
  const effortInverted = effort === 0 ? 0 : 4 - effort;

  return impact + priority + effortInverted;
};

/**
 * Hook for fetching and calculating top roadmap tasks by score
 * Returns top 3 tasks with highest combined score
 *
 * @returns {object} Object with topTasks array, error, and isLoading state
 *
 */
export function useRoadmapTopTasks() {
  const { data: roadmapItems, error, isLoading } = useRoadmapItems();

  const topTasks = useMemo(() => {
    if (!roadmapItems || roadmapItems.length === 0) {
      return [];
    }

    // Filter out done tasks - only show open tasks
    const openTasks = roadmapItems.filter((task) => task.status !== "done");

    if (openTasks.length === 0) {
      return [];
    }

    // Calculate score for each task and sort
    const tasksWithScores = openTasks.map((task) => ({
      rank: 0, // Will be set after sorting
      score: calculateTaskScore(task),
      task,
    }));

    // Reason: toSorted() is preferred but TypeScript may not recognize it in all versions - using sort() on copy instead
    // eslint-disable-next-line unicorn/no-array-sort -- Reason: toSorted() not recognized by TypeScript
    const sortedTasks = [...tasksWithScores].sort((a, b) => b.score - a.score);
    const topThreeTasks = sortedTasks.slice(0, 3);

    const tasksWithScore: RoadmapTaskWithScore[] = topThreeTasks.map((item, index) => ({
      ...item,
      rank: index + 1,
    }));

    return tasksWithScore;
  }, [roadmapItems]);

  return {
    error,
    isLoading,
    topTasks,
  };
}
